import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { formatNumber } from "@formant/core/common/formatNumber";
import { duration } from "@formant/core/model/duration";

import { useStores } from "../../../context/StoreContext";
import { Select } from "../../common/Select";
import styles from "./index.scss";

function snapToDuration(timelineScale: number): number {
    const { day, hour, minute, second } = duration;
    const { round } = Math;
    if (timelineScale >= day) {
        return round(timelineScale / day) * day;
    } else if (timelineScale >= hour) {
        return round(timelineScale / hour) * hour;
    } else if (timelineScale >= minute) {
        return round(timelineScale / minute) * minute;
    } else if (timelineScale >= second) {
        return round(timelineScale / second) * second;
    } else {
        return timelineScale;
    }
}

function intervalLabel(timelineScale: number): string {
    const { day, hour, minute, second } = duration;
    const { round } = Math;

    if (timelineScale >= duration.day) {
        const days = round(timelineScale / day);
        return `${formatNumber(days, true, 0)} day${days > 1 ? "s" : ""}`;
    } else if (timelineScale >= hour) {
        const hours = round(timelineScale / hour);
        return `${formatNumber(hours, true, 0)} hr${hours > 1 ? "s" : ""}`;
    } else if (timelineScale >= minute) {
        const minutes = round(timelineScale / minute);
        return `${formatNumber(minutes, true, 0)} min`;
    } else if (timelineScale >= second) {
        const seconds = round(timelineScale / second);
        return `${formatNumber(seconds, true, 0)} sec`;
    } else {
        return `${formatNumber(timelineScale, true, 0)} ms`;
    }
}

const analyticsTimescaleOptions = [
    { label: "Last 7 days", value: 7 * duration.day },
    { label: "Last 30 days", value: 30 * duration.hour },
    { label: "Last 90 days", value: 90 * duration.day },
];

const defaultTimescaleOptions = [
    { label: "24 hrs", value: 24 * duration.hour },
    { label: "6 hrs", value: 6 * duration.hour },
    { label: "1 hr", value: duration.hour },
    { label: "30 min", value: 30 * duration.minute },
    { label: "10 min", value: 10 * duration.minute },
    { label: "5 min", value: 5 * duration.minute },
    { label: "1 min", value: duration.minute },
    { label: "30 sec", value: 30 * duration.second },
    { label: "5 sec", value: 5 * duration.second },
];

export interface ITimescaleSelectProps {
    analyticsOnly: boolean;
}

export const TimescaleSelect = observer(function TimescaleSelect(
    props: ITimescaleSelectProps,
) {
    const { analyticsOnly } = props;
    const { appStore } = useStores();

    const timescaleOptions = analyticsOnly
        ? analyticsTimescaleOptions
        : defaultTimescaleOptions;

    const customIntervalLabel = intervalLabel(
        snapToDuration(appStore.timelineScale),
    );

    const timescaleLabel = useCallback(
        (_: number) =>
            timescaleOptions.find(option => option.value === _)?.label ||
            customIntervalLabel,
        [timescaleOptions, customIntervalLabel],
    );

    const selectOptions = useMemo(
        () => timescaleOptions.map(_ => _.value),
        [timescaleOptions],
    );

    const handleChangeInterval = useCallback(
        (value: number) => {
            appStore.timelineScale = value;
        },
        [appStore],
    );

    return (
        <Select
            value={snapToDuration(appStore.timelineScale)}
            options={selectOptions}
            bottom={true}
            label={timescaleLabel}
            icon="duration"
            title="Timeline interval"
            responsive={true}
            className={classnames(styles.interval, styles.button)}
            onChange={handleChangeInterval}
            labelOverride={
                !timescaleOptions.find(_ => _.label === customIntervalLabel)
                    ? customIntervalLabel
                    : undefined
            }
        />
    );
});
